import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Plus, Check, X, Loader2, RefreshCcw, Trash2, Mail, Phone, Car,
} from "lucide-react";
import { api } from "../../../lib/api";

const SOURCES = ["instagram","tiktok","facebook","google","website","referral","repeat","phone","walkin","business_referral","event","other"];

/**
 * CRMLeadsTab — pending (needs staff approval) + approved lead list.
 * Abandoned booking-intents auto-become pending leads via
 * POST /admin/crm/leads/sweep-intents (run on tab load + on-demand).
 */
export default function CRMLeadsTab() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Sweep first so anything abandoned lands in pending
      await api.post("/admin/crm/leads/sweep-intents").catch(() => {});
      const [p, a] = await Promise.all([
        api.get("/admin/crm/leads?status=pending"),
        api.get("/admin/crm/leads?status=approved"),
      ]);
      setPending(p.data || []);
      setApproved(a.data || []);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.patch(`/admin/crm/leads/${id}`, { approved: true, stage: "new_lead" });
    toast.success("Lead approved — sent to pipeline");
    load();
  };
  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this lead?")) return;
    await api.delete(`/admin/crm/leads/${id}`);
    load();
  };

  return (
    <div data-testid="crm-leads-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="label-caps text-cyan text-[10px]">Leads</div>
          <h3 className="font-serif text-2xl text-white mt-0.5">Pending &amp; active leads</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="crm-leads-refresh"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-white/70 text-xs hover:bg-white/5 transition disabled:opacity-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />} Sweep
          </button>
          <button
            data-testid="crm-leads-add"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan text-black text-xs font-bold label-caps transition"
          >
            <Plus size={12} /> New lead
          </button>
        </div>
      </div>

      {showForm && (
        <NewLeadForm
          creating={creating}
          setCreating={setCreating}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {/* Pending approval */}
      <Section title={`Awaiting approval · ${pending.length}`} tone="amber">
        {pending.length === 0 ? (
          <div className="p-6 text-center text-white/50 text-xs italic">No pending leads. Abandoned bookings will appear here.</div>
        ) : (
          pending.map((l) => (
            <LeadRow key={l.id} lead={l}
              actions={
                <>
                  <button
                    data-testid={`crm-lead-approve-${l.id}`}
                    onClick={() => approve(l.id)}
                    className="p-1.5 rounded-md bg-lime-400/20 text-lime-300 hover:bg-lime-400/30 transition"
                    title="Approve"
                  ><Check size={13} /></button>
                  <button
                    data-testid={`crm-lead-dismiss-${l.id}`}
                    onClick={() => dismiss(l.id)}
                    className="p-1.5 rounded-md text-white/40 hover:text-red-300 transition"
                    title="Dismiss"
                  ><Trash2 size={13} /></button>
                </>
              }
            />
          ))
        )}
      </Section>

      {/* Approved */}
      <Section title={`Active leads · ${approved.length}`} tone="cyan">
        {approved.length === 0 ? (
          <div className="p-6 text-center text-white/50 text-xs italic">No active leads yet.</div>
        ) : (
          approved.map((l) => (
            <LeadRow key={l.id} lead={l}
              actions={
                <span className="label-caps text-[9px] text-cyan px-2 py-0.5 rounded-full bg-cyan/15">
                  {(l.stage || "new_lead").replace(/_/g, " ")}
                </span>
              }
            />
          ))
        )}
      </Section>
    </div>
  );
}

function Section({ title, tone, children }) {
  const tones = { amber: "border-amber-400/30", cyan: "border-cyan/25" };
  return (
    <div className={`rounded-2xl border ${tones[tone]} bg-black/25 overflow-hidden`}>
      <div className="p-3 border-b border-white/5 label-caps text-[10px] text-white/60">{title}</div>
      <div className="divide-y divide-white/5">{children}</div>
    </div>
  );
}

function LeadRow({ lead, actions }) {
  return (
    <div className="p-3 flex items-center gap-3 hover:bg-cyan/[0.04] transition"
         data-testid={`crm-lead-row-${lead.id}`}>
      <div className="w-8 h-8 rounded-full bg-cyan/15 border border-cyan/30 flex items-center justify-center shrink-0 text-cyan text-xs font-bold">
        <Sparkles size={13} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">
          {lead.name || <span className="text-white/40 italic">Unknown</span>}
          <span className="text-white/40 text-[10px] font-normal ml-2">· {lead.source || "manual"}</span>
        </div>
        <div className="text-white/50 text-[11px] flex items-center gap-3 flex-wrap mt-0.5">
          {lead.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {lead.email}</span>}
          {lead.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {lead.phone}</span>}
          {lead.vehicle_name && <span className="inline-flex items-center gap-1"><Car size={10} /> {lead.vehicle_name}</span>}
        </div>
        {lead.notes && <div className="text-white/40 text-[10px] mt-0.5 truncate">{lead.notes}</div>}
      </div>
      <div className="text-right shrink-0 flex items-center gap-1">{actions}</div>
    </div>
  );
}

function NewLeadForm({ creating, setCreating, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", source: "phone",
    service: "chauffeur", vehicle_name: "", requested_dates: "",
    estimated_value: "", notes: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/crm/leads", {
        ...form,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
      });
      toast.success("Lead added to pipeline");
      onCreated();
    } catch {
      toast.error("Failed to create lead");
    } finally {
      setCreating(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center" onClick={onClose}>
      <form onSubmit={submit}
        data-testid="crm-lead-new-form"
        className="bg-ink-900 border border-white/10 rounded-2xl max-w-lg w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-xl">New lead</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Field label="Source">
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none">
              {SOURCES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" />
          <Field label="Service">
            <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
              className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none">
              <option value="chauffeur">Chauffeur ride</option>
              <option value="rental">Self-drive rental</option>
            </select>
          </Field>
          <Field label="Est. value ($)" value={form.estimated_value} onChange={(v) => setForm({ ...form, estimated_value: v })} type="number" />
        </div>
        <Field label="Vehicle interest" value={form.vehicle_name} onChange={(v) => setForm({ ...form, vehicle_name: v })} />
        <Field label="Requested dates" value={form.requested_dates} onChange={(v) => setForm({ ...form, requested_dates: v })} placeholder="e.g. Sat Aug 15, 8pm" />
        <Field label="Notes">
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none resize-none" />
        </Field>
        <button type="submit" disabled={creating}
          data-testid="crm-lead-new-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cyan text-black font-bold text-xs label-caps tracking-widest disabled:opacity-50">
          {creating ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Plus size={13} /> Add to pipeline</>}
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, children }) {
  return (
    <label className="block">
      <span className="label-caps text-[9px] text-white/60 tracking-widest">{label}</span>
      {children || (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="mt-1 w-full bg-black/50 border border-white/15 focus:border-cyan/60 rounded-lg px-3 py-2 text-white text-sm outline-none"
        />
      )}
    </label>
  );
}
